import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray, AbstractControl, ValidationErrors } from '@angular/forms';
import { VesselCalculationInventoryService } from 'src/app/services/danish-shipping-services/vessel-calculation-inventory.service';
import { ToastrService } from 'ngx-toastr';
import { catchError } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-vessel-subgroup-update',
  templateUrl: './vessel-subgroup-update.component.html',
  styleUrls: ['./vessel-subgroup-update.component.css']
})
export class VesselSubgroupUpdateComponent implements OnInit {
  vesselSubgroupForm: FormGroup;
  isSubmitting = false;
  errors: any = {};

  vesselType: string = '';
  subType: string = '';

  constructor(
    private fb: FormBuilder,
    private vesselService: VesselCalculationInventoryService,
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.vesselSubgroupForm = this.fb.group({
      subType: ['', Validators.required],
      lifetime: [0, [Validators.required, Validators.min(0)]],   
      conversionFactor: [0, [Validators.required, Validators.min(0)]],
      subgroups: this.fb.array([], this.validateWeightDistributionSum.bind(this))
    });
  }

  ngOnInit(): void {
    this.vesselType = this.route.snapshot.paramMap.get('vesselType') || '';
    this.subType = this.route.snapshot.paramMap.get('subType') || '';

    if (this.vesselType && this.subType) {
      this.loadExistingSubgroups();
    } else {
      this.toastr.warning('No vesselType or subType specified.');
    }
  }

  validateWeightDistributionSum(formArray: AbstractControl): ValidationErrors | null {
    const subgroups = formArray.value;
    if (!subgroups || subgroups.length === 0) return null;

    const totalWeight = subgroups.reduce((sum: number, subgroup: any) => {
      if (subgroup.description?.trim().toLowerCase() === 'assembly') {
        return sum;
      }

      const weight = parseFloat(subgroup.weightDistribution);
      return sum + (isNaN(weight) ? 0 : weight);
    }, 0);

    return Math.round(totalWeight) !== 100 ? { weightDistributionNotEqual: true } : null;
  }

  get subgroups(): FormArray {
    return this.vesselSubgroupForm.get('subgroups') as FormArray;
  }

  loadExistingSubgroups(): void {
    this.vesselService.getVesselSubgroups(this.vesselType, this.subType).subscribe({
      next: (response) => {
        this.subgroups.clear();

        const subgroups = response?.subgroups;
        const conversionFactor = subgroups?.length > 0 ? subgroups[0].conversionFactor : 0;
        const lifetime = response?.lifeTime ?? 0;  

        if (subgroups && subgroups.length > 0) {
          this.vesselSubgroupForm.patchValue({
            conversionFactor,
            subType: this.subType,
            lifetime   
          });

          subgroups.forEach((sg: any) => {
            this.subgroups.push(this.fb.group({
              sfiCode: [sg.sfiCode],
              description: [sg.description],
              coefficient: [sg.coefficient, [Validators.required, Validators.min(0)]],
              weightDistribution: [sg.weightDistribution, [Validators.required, Validators.min(0)]],
            }));
          });

          this.subgroups.controls.forEach((group: FormGroup) => {
            const desc = group.get('description')?.value?.trim().toLowerCase();
            if (desc === 'assembly') {
              group.get('weightDistribution')?.disable();
            } else {
              group.get('weightDistribution')?.enable();
            }
          });

        } else {
          this.toastr.warning('No subgroups found in the response.');
          this.vesselSubgroupForm.patchValue({
            lifetime   
          });
        }
      },
      error: () => {
        this.toastr.error('Failed to load existing subgroups');
      }
    });
  }

  updateVesselSubgroups(): void {
    if (this.isSubmitting) return;

    this.isSubmitting = true;
    this.errors = {};

    if (this.vesselSubgroupForm.invalid) {
      this.toastr.error('Please fill all required fields. Total weight distribution must equal 100%.');
      this.isSubmitting = false;
      return;
    }

    const dto = {
      vesselType: this.vesselType,
      subType: this.vesselSubgroupForm.get('subType')?.value,
      newLifeTime: this.vesselSubgroupForm.get('lifetime')?.value,   
      conversionFactor: this.vesselSubgroupForm.get('conversionFactor')?.value,
      subgroups: this.subgroups.getRawValue()
    };

    this.vesselService.updateVesselSubgroups(this.vesselType, this.subType, dto)
      .pipe(
        catchError(error => {
          this.errors = {};
          if (error.status === 400) {
            if (Array.isArray(error.error)) {
              error.error.forEach((errMessage: string) => {
                const matchSubgroup = errMessage.match(/subgroups\[(\d+)\]\.(\w+)/);
                if (matchSubgroup) {
                  const index = matchSubgroup[1];
                  const field = matchSubgroup[2];
                  const key = `subgroups.${index}.${field}`;
                  this.errors[key] = this.errors[key] || [];
                  this.errors[key].push(errMessage);
                } else if (errMessage.includes('conversionFactor')) {
                  this.errors['conversionFactor'] = this.errors['conversionFactor'] || [];
                  this.errors['conversionFactor'].push(errMessage);
                } else if (errMessage.includes('lifetime')) {
                  this.errors['lifetime'] = this.errors['lifetime'] || [];
                  this.errors['lifetime'].push(errMessage);
                } else {
                  this.errors.general = this.errors.general || [];
                  this.errors.general.push(errMessage);
                }
              });
            } else if (typeof error.error === 'string') {
              this.errors.general = [error.error];
            } else {
              this.toastr.error('An unexpected error occurred.');
            }
          } else {
            this.toastr.error('Failed to update vessel subgroups.');
          }
          this.isSubmitting = false;
          throw error;
        })
      )
      .subscribe({
        next: () => {
          this.toastr.success('Vessel subgroups updated successfully!');
          this.router.navigate(['/homeAdmin/v2/vesselSubgroup']);
        },
        error: () => {
          this.isSubmitting = false;
        }
      });
  }

  get weightDistributionTotal(): number {
    if (!this.subgroups || this.subgroups.length === 0) return 0;

    return this.subgroups.controls.reduce((sum, subgroup) => {
      const desc = subgroup.get('description')?.value?.trim().toLowerCase();
      if (desc === 'assembly') return sum;

      const weight = parseFloat(subgroup.get('weightDistribution')?.value);
      return sum + (isNaN(weight) ? 0 : weight);
    }, 0);
  }

  get isWeightDistributionValid(): boolean {
    return Math.round(this.weightDistributionTotal) === 100;
  }
}
